import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, RadialLinearScale, PointElement, LineElement, Filler } from 'chart.js';
import { Doughnut, Bar, Radar, Line } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faShieldAlt, 
  faExclamationTriangle, 
  faSearch, 
  faCrosshairs, 
  faChartLine,
  faProjectDiagram,
  faFolder,
  faBullseye,
  faCheckCircle,
  faMapMarkedAlt,
  faFlask,
  faMedal,
  faSync,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import projectService from '../services/projectService';
import actionService from '../services/actionService';
import targetService from '../services/targetService';
import campaignService from '../services/campaignService';
import atomicService from '../services/atomicService';
import epssService from '../services/epssService';
import targetTypesService from '../services/targetTypesService';
import { useLanguage } from '../contexts/LanguageContext';
import PageHeader from '../components/common/PageHeader';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, RadialLinearScale, PointElement, LineElement, Filler);

const ExecutiveDashboard = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [actions, setActions] = useState([]);
  const [targets, setTargets] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [atomicTests, setAtomicTests] = useState([]);
  const [epssStats, setEpssStats] = useState(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState('30days');

  useEffect(() => {
    fetchDashboardData();
  }, [selectedTimeframe]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch projects first
      const projectsData = await projectService.getProjects();
      const projectsArray = Array.isArray(projectsData) ? projectsData : [];
      setProjects(projectsArray);

      // Fetch all actions
      const actionsData = await actionService.getActions();
      const actionsArray = Array.isArray(actionsData) ? actionsData : [];
      setActions(filterByTimeframe(actionsArray));
      
      // Fetch targets for all projects
      const allTargets = [];
      for (const project of projectsArray) {
        try {
          const projectTargets = await targetService.getTargetsByProject(project._id);
          allTargets.push(...projectTargets);
        } catch (error) {
          console.error(`Error fetching targets for project ${project._id}:`, error);
        }
      }
      setTargets(allTargets);
      
      // Fetch campaigns
      try {
        const campaignsData = await campaignService.getCampaigns();
        const campaignsArray = Array.isArray(campaignsData) ? campaignsData : [];
        setCampaigns(filterByTimeframe(campaignsArray));
      } catch (error) {
        console.error('Error fetching campaigns:', error);
        setCampaigns([]);
      }

      // Fetch atomic tests
      try {
        const atomicData = await atomicService.getAtomicTests();
        setAtomicTests(Array.isArray(atomicData) ? atomicData : []);
      } catch (error) {
        console.error('Error fetching atomic tests:', error);
        setAtomicTests([]);
      }

      // Fetch EPSS stats - handle if service not available
      if (projectsArray.length > 0 && epssService.getProjectEPSSStats) {
        try {
          const epssPromises = projectsArray.map(p => 
            epssService.getProjectEPSSStats(p._id).catch(err => {
              console.error(`EPSS error for project ${p._id}:`, err);
              return null;
            })
          );
          const allEpssStats = await Promise.allSettled(epssPromises);
          const combinedStats = allEpssStats
            .filter(result => result.status === 'fulfilled' && result.value)
            .reduce((acc, result) => {
              const stats = result.value;
              return {
                totalCves: (acc.totalCves || 0) + (stats.totalCves || 0),
                highRiskCves: (acc.highRiskCves || 0) + (stats.highRiskCves || 0),
                criticalCves: (acc.criticalCves || 0) + (stats.criticalCves || 0),
                averageEpss: acc.averageEpss ? 
                  ((acc.averageEpss + parseFloat(stats.averageEpss || 0)) / 2).toFixed(3) : 
                  stats.averageEpss || '0.000',
                criticalTargets: [...(acc.criticalTargets || []), ...(stats.criticalTargets || [])]
              };
            }, {});
          setEpssStats(combinedStats);
        } catch (error) {
          console.error('Error fetching EPSS stats:', error);
          setEpssStats(null);
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const filterByTimeframe = (data) => {
    if (!data || data.length === 0) return [];
    if (selectedTimeframe === 'all') return data;
    
    const now = new Date();
    const cutoffDate = new Date();
    
    switch (selectedTimeframe) {
      case '7days':
        cutoffDate.setDate(now.getDate() - 7);
        break;
      case '30days':
        cutoffDate.setDate(now.getDate() - 30);
        break;
      case '90days':
        cutoffDate.setDate(now.getDate() - 90);
        break;
      case 'year':
        cutoffDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        return data;
    }
    
    return data.filter(item => {
      const itemDate = new Date(item.startTimestamp || item.timestamp || item.createdAt);
      return !isNaN(itemDate.getTime()) && itemDate >= cutoffDate;
    });
  };

  // Enhanced metrics calculations with safety checks
  const calculateMetrics = () => {
    const metrics = {
      // Basic counts
      totalProjects: projects.length || 0,
      activeProjects: projects.filter(p => p.status === 'in-progress').length || 0,
      totalActions: actions.length || 0,
      totalTargets: targets.length || 0,
      totalCampaigns: campaigns.length || 0,
      activeCampaigns: campaigns.filter(c => c.status === 'active').length || 0,
      
      // Success metrics
      successfulActions: actions.filter(a => a.result === 'successful').length || 0,
      detectedActions: actions.filter(a => a.result === 'detected').length || 0,
      blockedActions: actions.filter(a => a.result === 'blocked').length || 0,
      
      // Target type distribution
      targetTypes: {},
      criticalTargets: 0,
      
      // MITRE coverage
      uniqueTechniques: new Set(),
      uniqueTactics: new Set(),
      techniqueSuccess: {},
      
      // Operator metrics
      operatorStats: {},
      
      // Time-based metrics
      actionsByDay: {},
      avgActionsPerDay: 0,
      
      // Risk metrics
      criticalFindings: [],
      highValueTargetsCompromised: 0,
      
      // Atomic test coverage
      atomicCoverage: 0
    };

    // Calculate target type distribution
    if (targets && targets.length > 0) {
      targets.forEach(target => {
        const type = target.type || 'unknown';
        metrics.targetTypes[type] = (metrics.targetTypes[type] || 0) + 1;
        if (target.metadata?.criticality === 'critical') {
          metrics.criticalTargets++;
        }
      });
    }

    // Calculate MITRE metrics
    if (actions && actions.length > 0) {
      actions.forEach(action => {
        if (action.mitreAttackTechnique) {
          metrics.uniqueTechniques.add(action.mitreAttackTechnique.id);
          metrics.uniqueTactics.add(action.mitreAttackTechnique.tactic);
          
          const techId = action.mitreAttackTechnique.id;
          if (!metrics.techniqueSuccess[techId]) {
            metrics.techniqueSuccess[techId] = { total: 0, successful: 0 };
          }
          metrics.techniqueSuccess[techId].total++;
          if (action.result === 'successful') {
            metrics.techniqueSuccess[techId].successful++;
          }
        }
        
        // Operator stats
        if (action.operator) {
          if (!metrics.operatorStats[action.operator]) {
            metrics.operatorStats[action.operator] = { total: 0, successful: 0 };
          }
          metrics.operatorStats[action.operator].total++;
          if (action.result === 'successful') {
            metrics.operatorStats[action.operator].successful++;
          }
        }
        
        // Time-based analysis
        const actionDate = action.startTimestamp || action.timestamp;
        if (actionDate) {
          const date = new Date(actionDate).toISOString().split('T')[0];
          metrics.actionsByDay[date] = (metrics.actionsByDay[date] || 0) + 1;
        }
      });
    }

    // Calculate averages
    const daysWithActions = Object.keys(metrics.actionsByDay).length;
    metrics.avgActionsPerDay = daysWithActions > 0 ? 
      (metrics.totalActions / daysWithActions).toFixed(1) : 0;

    // Identify critical findings
    const criticalTechniques = ['T1003', 'T1078', 'T1486', 'T1490', 'T1098', 'T1136'];
    metrics.criticalFindings = actions.filter(a => 
      a.result === 'successful' && 
      a.mitreAttackTechnique && 
      criticalTechniques.includes(a.mitreAttackTechnique.id)
    );

    // Calculate atomic test coverage
    if (atomicTests && atomicTests.length > 0 && metrics.uniqueTechniques.size > 0) {
      const techniquesWithAtomic = new Set(atomicTests.map(t => t.techniqueId));
      const coveredTechniques = Array.from(metrics.uniqueTechniques).filter(t => 
        techniquesWithAtomic.has(t)
      ).length;
      metrics.atomicCoverage = Math.round((coveredTechniques / metrics.uniqueTechniques.size) * 100);
    }

    return metrics;
  };

  const metrics = !loading ? calculateMetrics() : {};

  // Calculate Security Score (0-100)
  const calculateSecurityScore = () => {
    if (!actions || actions.length === 0) return 100;
    
    const weights = {
      blocked: 100,
      detected: 70,
      failed: 50,
      successful: 0
    };
    
    let totalScore = 0;
    actions.forEach(action => {
      totalScore += weights[action.result] || 0;
    });
    
    const maxPossibleScore = actions.length * 100;
    return Math.round((totalScore / maxPossibleScore) * 100);
  };

  // Enhanced Risk Level calculation
  const calculateRiskLevel = () => {
    let riskScore = 0;
    
    // Factor 1: Critical technique success rate
    if (metrics.criticalFindings) {
      riskScore += metrics.criticalFindings.length * 20;
    }
    
    // Factor 2: EPSS scores
    if (epssStats?.criticalCves > 0) {
      riskScore += epssStats.criticalCves * 15;
    }
    
    // Factor 3: Critical targets compromised
    const criticalTargetsCompromised = actions.filter(a => 
      a.result === 'successful' && 
      a.targets?.some(tid => {
        const target = targets.find(t => t._id === tid || t._id === tid._id);
        return target?.metadata?.criticality === 'critical';
      })
    ).length;
    riskScore += criticalTargetsCompromised * 10;
    
    // Factor 4: Detection rate (inverse)
    const detectionRate = metrics.totalActions > 0 ?
      ((metrics.detectedActions + metrics.blockedActions) / metrics.totalActions) : 1;
    riskScore += (1 - detectionRate) * 50;
    
    if (riskScore <= 20) return { level: 'Low', color: 'green', score: riskScore };
    if (riskScore <= 50) return { level: 'Medium', color: 'yellow', score: riskScore };
    if (riskScore <= 80) return { level: 'High', color: 'orange', score: riskScore };
    return { level: 'Critical', color: 'red', score: Math.min(100, riskScore) };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const securityScore = calculateSecurityScore();
  const riskLevel = calculateRiskLevel();

  // Debug logging
  console.log('Dashboard Data:', {
    projects: projects.length,
    actions: actions.length,
    targets: targets.length,
    campaigns: campaigns.length,
    metrics
  });

  // Chart configurations with safety checks
  const actionTrendChart = {
    labels: Object.keys(metrics.actionsByDay || {}).sort().slice(-30), // Last 30 days
    datasets: [{
      label: 'Actions per Day',
      data: Object.keys(metrics.actionsByDay || {}).sort().slice(-30).map(date => metrics.actionsByDay[date] || 0),
      borderColor: '#0066cc',
      backgroundColor: 'rgba(0, 102, 204, 0.2)',
      tension: 0.1
    }]
  };

  const targetTypeChart = {
    labels: Object.entries(metrics.targetTypes || {}).map(([type, _]) => 
      targetTypesService.getTargetType(type).name
    ),
    datasets: [{
      data: Object.values(metrics.targetTypes || {}),
      backgroundColor: [
        'rgba(0, 102, 204, 0.8)',
        'rgba(0, 128, 255, 0.8)',
        'rgba(77, 148, 255, 0.8)',
        'rgba(102, 163, 255, 0.8)',
        'rgba(26, 117, 255, 0.8)',
        'rgba(0, 82, 204, 0.8)',
        'rgba(51, 133, 255, 0.8)',
        'rgba(0, 92, 230, 0.8)',
      ],
    }],
  };

  const operatorPerformanceChart = {
    labels: Object.keys(metrics.operatorStats || {}),
    datasets: [
      {
        label: 'Total Actions',
        data: Object.values(metrics.operatorStats || {}).map(s => s.total || 0),
        backgroundColor: 'rgba(0, 102, 204, 0.5)',
      },
      {
        label: 'Successful Actions',
        data: Object.values(metrics.operatorStats || {}).map(s => s.successful || 0),
        backgroundColor: 'rgba(40, 167, 69, 0.5)',
      }
    ]
  };

  // Action results chart
  const actionResultsChart = {
    labels: ['Successful', 'Detected', 'Blocked', 'Failed', 'Unknown'],
    datasets: [{
      data: [
        metrics.successfulActions || 0,
        metrics.detectedActions || 0,
        metrics.blockedActions || 0,
        actions.filter(a => a.result === 'failed').length || 0,
        actions.filter(a => !a.result || a.result === 'unknown').length || 0
      ],
      backgroundColor: [
        'rgba(40, 167, 69, 0.8)',   // green
        'rgba(255, 193, 7, 0.8)',   // yellow
        'rgba(220, 53, 69, 0.8)',   // red
        'rgba(108, 117, 125, 0.8)', // gray
        'rgba(102, 16, 242, 0.8)',  // purple
      ],
    }],
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <PageHeader
        title={t('executive.title')}
        subtitle={t('executive.subtitle')}
      />

      {/* Timeframe Selector */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex space-x-2">
          {['7days', '30days', '90days', 'year', 'all'].map(timeframe => (
            <button
              key={timeframe}
              onClick={() => setSelectedTimeframe(timeframe)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                selectedTimeframe === timeframe
                  ? 'bg-primary text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {timeframe === '7days' ? t('executive.last7Days') :
               timeframe === '30days' ? t('executive.last30Days') :
               timeframe === '90days' ? t('executive.last90Days') :
               timeframe === 'year' ? t('executive.lastYear') : t('executive.allTime')}
            </button>
          ))}
        </div>
        <button
          onClick={fetchDashboardData}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 flex items-center"
        >
          <FontAwesomeIcon icon={faSync} className="mr-2" />
          {t('common.refresh')}
        </button>
      </div>

      {/* Primary Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Security Score */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center text-primary">
            <FontAwesomeIcon icon={faShieldAlt} className="mr-2" />
            {t('executive.securityScore')}
          </h3>
          <div className="relative">
            <div className="text-5xl font-bold text-center mb-2" style={{
              color: securityScore >= 70 ? '#28a745' : securityScore >= 40 ? '#ffc107' : '#dc3545'
            }}>
              {securityScore}
            </div>
            <div className="text-center text-gray-500">{t('executive.outOf100')}</div>
            <div className="mt-4 bg-gray-200 rounded-full h-4">
              <div 
                className="h-4 rounded-full transition-all duration-500"
                style={{
                  width: `${securityScore}%`,
                  backgroundColor: securityScore >= 70 ? '#28a745' : securityScore >= 40 ? '#ffc107' : '#dc3545'
                }}
              />
            </div>
          </div>
        </div>

        {/* Risk Level */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center text-primary">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
            {t('executive.riskAssessment')}
          </h3>
          <div className="text-center">
            <div className={`text-4xl font-bold mb-2 ${
              riskLevel.color === 'green' ? 'text-green-600' :
              riskLevel.color === 'yellow' ? 'text-yellow-600' :
              riskLevel.color === 'orange' ? 'text-orange-600' :
              'text-red-600'
            }`}>
              {riskLevel.level}
            </div>
            <div className="text-sm text-gray-500 mb-2">
              {t('executive.riskScore')}: {riskLevel.score}/100
            </div>
            <div className="text-xs text-gray-400">
              {t('executive.basedOnFindings', { count: metrics.criticalFindings?.length || 0 })}
            </div>
          </div>
        </div>

        {/* Detection Rate */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center text-primary">
            <FontAwesomeIcon icon={faSearch} className="mr-2" />
            {t('executive.detectionRate')}
          </h3>
          <div className="text-center">
            <div className="text-5xl font-bold text-primary mb-2">
              {metrics.totalActions > 0 
                ? Math.round(((metrics.detectedActions + metrics.blockedActions) / metrics.totalActions) * 100)
                : 0}%
            </div>
            <div className="text-sm text-gray-500">
              {t('executive.detectedBlocked', { detected: metrics.detectedActions + metrics.blockedActions, total: metrics.totalActions })}
            </div>
          </div>
        </div>

        {/* EPSS Risk Summary */}
        {epssStats && Object.keys(epssStats).length > 0 ? (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center text-primary">
              <FontAwesomeIcon icon={faCrosshairs} className="mr-2" />
              {t('executive.exploitRisk')}
            </h3>
            <div className="text-center">
              <div className="text-5xl font-bold text-purple-600 mb-2">
                {epssStats.criticalCves || 0}
              </div>
              <div className="text-sm text-gray-500">
                {t('executive.criticalCves')}
              </div>
              <div className="text-xs text-gray-400 mt-1">
                {t('executive.avgEpss')}: {epssStats.averageEpss || '0.000'}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center text-primary">
              <FontAwesomeIcon icon={faChartLine} className="mr-2" />
              {t('executive.activeOperations')}
            </h3>
            <div className="text-center">
              <div className="text-5xl font-bold text-purple-600 mb-2">
                {metrics.activeProjects}
              </div>
              <div className="text-sm text-gray-500">
                {t('executive.projectsInProgress')}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Secondary Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <FontAwesomeIcon icon={faFolder} className="text-2xl text-primary mb-2" />
          <div className="text-2xl font-bold text-gray-800">{metrics.totalProjects}</div>
          <div className="text-sm text-gray-600">{t('executive.totalProjects')}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <FontAwesomeIcon icon={faBullseye} className="text-2xl text-primary mb-2" />
          <div className="text-2xl font-bold text-gray-800">{metrics.totalActions}</div>
          <div className="text-sm text-gray-600">{t('executive.totalActions')}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <FontAwesomeIcon icon={faCheckCircle} className="text-2xl text-primary mb-2" />
          <div className="text-2xl font-bold text-gray-800">{metrics.uniqueTechniques?.size || 0}</div>
          <div className="text-sm text-gray-600">{t('executive.mitreTechniques')}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <FontAwesomeIcon icon={faBullseye} className="text-2xl text-primary mb-2" />
          <div className="text-2xl font-bold text-gray-800">{metrics.totalTargets}</div>
          <div className="text-sm text-gray-600">{t('executive.totalTargets')}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-2xl text-primary mb-2" />
          <div className="text-2xl font-bold text-gray-800">{metrics.criticalTargets}</div>
          <div className="text-sm text-gray-600">{t('executive.criticalTargets')}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <FontAwesomeIcon icon={faFlask} className="text-2xl text-primary mb-2" />
          <div className="text-2xl font-bold text-gray-800">{metrics.atomicCoverage}%</div>
          <div className="text-sm text-gray-600">{t('executive.atomicCoverage')}</div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Action Results Distribution */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 text-primary">{t('executive.actionResults')}</h3>
          {metrics.totalActions > 0 ? (
            <div style={{ height: '300px' }}>
              <Doughnut
                data={actionResultsChart}
                options={{
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'bottom' }
                  }
                }}
              />
            </div>
          ) : (
            <p className="text-gray-500 text-center py-10">{t('executive.noActionData')}</p>
          )}
        </div>

        {/* Action Trend */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 text-primary">{t('executive.actionTrend')}</h3>
          {Object.keys(metrics.actionsByDay || {}).length > 0 ? (
            <div style={{ height: '300px' }}>
              <Line
                data={actionTrendChart}
                options={{
                  maintainAspectRatio: false,
                  scales: {
                    y: { beginAtZero: true }
                  }
                }}
              />
            </div>
          ) : (
            <p className="text-gray-500 text-center py-10">{t('executive.noTrendData')}</p>
          )}
        </div>

        {/* Target Type Distribution */}
        {Object.keys(metrics.targetTypes || {}).length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 text-primary">{t('executive.targetDistribution')}</h3>
            <div style={{ height: '300px' }}>
              <Doughnut
                data={targetTypeChart}
                options={{
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'right' }
                  }
                }}
              />
            </div>
          </div>
        )}

        {/* Operator Performance */}
        {Object.keys(metrics.operatorStats || {}).length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 text-primary">{t('executive.operatorPerformance')}</h3>
            <div style={{ height: '300px' }}>
              <Bar
                data={operatorPerformanceChart}
                options={{
                  maintainAspectRatio: false,
                  scales: {
                    y: { beginAtZero: true }
                  }
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Recent Critical Findings */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4 text-primary">{t('executive.criticalFindings')}</h3>
        {metrics.criticalFindings?.length > 0 ? (
          <div className="space-y-3">
            {metrics.criticalFindings.slice(0, 5).map((action, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div className="flex-1">
                  <div className="font-medium">{action.title}</div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{action.mitreAttackTechnique.id}</span> - 
                    {action.mitreAttackTechnique.name} • 
                    <span className="text-gray-500 ml-1">
                      {new Date(action.startTimestamp || action.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Project: {projects.find(p => p._id === action.project)?.name || 'Unknown'} • 
                    Operator: {action.operator}
                  </div>
                </div>
                <Link
                  to={`/projects/${action.project}?tab=actions`}
                  className="ml-4 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  {t('common.viewDetails')}
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-6">{t('executive.noCriticalFindings')}</p>
        )}
      </div>

      {/* Campaign Overview */}
      {campaigns.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 text-primary">{t('executive.activeCampaigns')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {campaigns
              .filter(c => c.status === 'active')
              .slice(0, 6)
              .map((campaign) => (
                <div key={campaign._id} className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium mb-2">{campaign.name}</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div>Actions: {campaign.actions?.length || 0}</div>
                    <div>Project: {projects.find(p => p._id === campaign.project)?.name || 'Unknown'}</div>
                    <div>Status: <span className="text-primary">{campaign.status}</span></div>
                  </div>
                  <Link
                    to={`/projects/${campaign.project}/campaigns`}
                    className="text-sm text-primary hover:text-primary-dark mt-2 inline-block"
                  >
                    View Campaign →
                  </Link>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="bg-gray-100 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 text-primary">{t('executive.summaryStats')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600">{t('executive.totalCampaigns')}:</span>
            <span className="ml-2 font-semibold">{metrics.totalCampaigns}</span>
          </div>
          <div>
            <span className="text-gray-600">{t('executive.activeCampaignsCount')}:</span>
            <span className="ml-2 font-semibold">{metrics.activeCampaigns}</span>
          </div>
          <div>
            <span className="text-gray-600">{t('executive.uniqueTactics')}:</span>
            <span className="ml-2 font-semibold">{metrics.uniqueTactics?.size || 0}</span>
          </div>
          <div>
            <span className="text-gray-600">{t('executive.avgActionsPerDay')}:</span>
            <span className="ml-2 font-semibold">{metrics.avgActionsPerDay}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveDashboard;